import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { faCreditCard, faLock } from '@fortawesome/free-solid-svg-icons';
import { PayjunctionService, BillingInfo, PaymentCardData } from '../../../services/payjunction.service';
import { ToastService } from '../../custom/messaging/toast/toast.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { RestApiService } from '../../../services/rest-api.service';

@Component({
  selector: 'app-payment-form',
  templateUrl: './payment-form.component.html',
  styleUrls: ['./payment-form.component.css']
})
export class PaymentFormComponent implements OnInit {
  faCreditCard = faCreditCard;
  faLock = faLock;

  paymentAmount: string = '';
  cardData: PaymentCardData = {
    cardNumber: '',
    cardExpMonth: '',
    cardExpYear: '',
    cardCvv: ''
  };

  billingInfo: BillingInfo = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  };

  errors: any = {};
  processing = false;
  step = 1;
  maxStep = 3;

  months = [
    { value: '01', label: '01 - January' },
    { value: '02', label: '02 - February' },
    { value: '03', label: '03 - March' },
    { value: '04', label: '04 - April' },
    { value: '05', label: '05 - May' },
    { value: '06', label: '06 - June' },
    { value: '07', label: '07 - July' },
    { value: '08', label: '08 - August' },
    { value: '09', label: '09 - September' },
    { value: '10', label: '10 - October' },
    { value: '11', label: '11 - November' },
    { value: '12', label: '12 - December' }
  ];

  years: string[] = [];

  constructor(
    public modal: NgbActiveModal,
    public payjunctionService: PayjunctionService,
    private toast: ToastService,
    private spinner: NgxSpinnerService,
    private rest: RestApiService
  ) {
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 20; i++) {
      this.years.push((currentYear + i).toString());
    }
  }

  ngOnInit(): void {
  }

  formatCardNumber(): void {
    this.cardData.cardNumber = this.payjunctionService.formatCardNumber(this.cardData.cardNumber);
  }

  formatPaymentAmount(): void {
    if (this.paymentAmount) {
      const numValue = parseFloat(this.paymentAmount);
      if (!isNaN(numValue)) {
        this.paymentAmount = numValue.toFixed(2);
      }
    }
  }

  onPaymentAmountInput(): void {
    // Remove any non-numeric characters except decimal point
    if (this.paymentAmount) {
      // Allow only numbers and one decimal point
      this.paymentAmount = this.paymentAmount.replace(/[^0-9.]/g, '');

      // Ensure only one decimal point
      const parts = this.paymentAmount.split('.');
      if (parts.length > 2) {
        this.paymentAmount = parts[0] + '.' + parts.slice(1).join('');
      }

      // Limit to 2 decimal places
      if (parts[1] && parts[1].length > 2) {
        this.paymentAmount = parts[0] + '.' + parts[1].substring(0, 2);
      }
    }
  }

  getFormattedPaymentAmount(): string {
    if (this.paymentAmount) {
      const numValue = parseFloat(this.paymentAmount);
      if (!isNaN(numValue)) {
        return numValue.toFixed(2);
      }
    }
    return '0.00';
  }

  validateStep1(): boolean {
    this.errors = {};

    if (!this.paymentAmount || !this.payjunctionService.validatePaymentAmount(this.paymentAmount)) {
      this.errors.amount = 'Please enter a valid payment amount';
      return false;
    }

    return true;
  }

  validateStep2(): boolean {
    this.errors = {};

    if (!this.payjunctionService.validateCardNumber(this.cardData.cardNumber)) {
      this.errors.cardNumber = 'Please enter a valid card number';
    }

    if (!this.payjunctionService.validateExpiryDate(this.cardData.cardExpMonth, this.cardData.cardExpYear)) {
      this.errors.expiry = 'Please enter a valid expiry date';
    }

    if (!this.cardData.cardCvv || this.cardData.cardCvv.length < 3) {
      this.errors.cvv = 'Please enter a valid CVV';
    }

    return Object.keys(this.errors).length === 0;
  }

  validateStep3(): boolean {
    this.errors = {};

    if (!this.billingInfo.firstName) {
      this.errors.firstName = 'First name is required';
    }

    if (!this.billingInfo.lastName) {
      this.errors.lastName = 'Last name is required';
    }

    if (!this.billingInfo.email) {
      this.errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.billingInfo.email)) {
      this.errors.email = 'Please enter a valid email address';
    }

    if (!this.billingInfo.address) {
      this.errors.address = 'Address is required';
    }

    if (!this.billingInfo.city) {
      this.errors.city = 'City is required';
    }

    if (!this.billingInfo.state) {
      this.errors.state = 'State is required';
    }

    if (!this.billingInfo.zip) {
      this.errors.zip = 'ZIP code is required';
    }

    return Object.keys(this.errors).length === 0;
  }

  async nextStep(): Promise<void> {
    let valid = false;

    switch (this.step) {
      case 1:
        valid = this.validateStep1();
        break;
      case 2:
        valid = this.validateStep2();
        break;
      case 3:
        valid = this.validateStep3();
        break;
    }

    if (valid && this.step < this.maxStep) {
      this.step++;
    } else if (valid && this.step === this.maxStep) {
      await this.processPayment();
    }
  }

  previousStep(): void {
    if (this.step > 1) {
      this.step--;
    }
  }

  async processPayment(): Promise<void> {
    this.processing = true;
    this.spinner.show();

    try {
      // Step 1: Create secure token from card data
      const tokenResponse = await this.payjunctionService.createPaymentToken(this.cardData);

      if (tokenResponse.errors) {
        // Handle tokenization errors
        this.spinner.hide();
        this.processing = false;
        const errorMessage = tokenResponse.errors.map(err => err.message).join(', ');
        this.toast.showDanger('Payment validation failed: ' + errorMessage);
        return;
      }

      if (!tokenResponse.tokenId) {
        this.spinner.hide();
        this.processing = false;
        this.toast.showDanger('Failed to create payment token. Please try again.');
        return;
      }

      // Step 2: Send payment data to backend for processing
      const paymentData = {
        tokenId: tokenResponse.tokenId,
        amount: this.paymentAmount,
        billing: {
          firstName: this.billingInfo.firstName,
          lastName: this.billingInfo.lastName,
          email: this.billingInfo.email,
          phone: this.billingInfo.phone,
          address: this.billingInfo.address,
          city: this.billingInfo.city,
          state: this.billingInfo.state,
          zip: this.billingInfo.zip
        }
      };

      const response = await this.rest.postTransaction(paymentData);

      this.spinner.hide();
      this.processing = false;

      if (response && response.status === 'success') {
        this.toast.showSuccess('Payment processed successfully!');
        // Pass back response with payment details for confirmation
        const responseWithPaymentDetails = {
          ...response,
          amount: this.paymentAmount,
          maskedCardNumber: this.payjunctionService.maskCardNumber(this.cardData.cardNumber)
        };
        this.modal.close(responseWithPaymentDetails);
      } else {
        this.toast.showDanger(response?.message || 'Payment failed. Please try again.');
        this.modal.dismiss('payment-failed');
      }
    } catch (error) {
      this.spinner.hide();
      this.processing = false;

      // Handle HTTP error responses with specific error messages
      console.log('Full error object:', error);
      console.log('Error status:', error.status);
      console.log('Error body:', error.error);

      if (error.status && error.error) {
        // Try to extract the message from various possible structures
        let errorMessage = 'Payment failed. Please try again.';

        if (error.error.detail && error.error.detail.message) {
          // Handle wrapped error in detail object (PayJunction specific errors)
          errorMessage = error.error.detail.message;
        } else if (error.error.message) {
          errorMessage = error.error.message;
        } else if (typeof error.error === 'string') {
          // Try to parse JSON string
          try {
            const parsed = JSON.parse(error.error);
            errorMessage = parsed.message || parsed.error || error.error;
          } catch {
            errorMessage = error.error;
          }
        } else if (error.error.error) {
          errorMessage = error.error.error;
        }

        // Don't add generic text if we have a specific payment-related error
        if (errorMessage !== 'Payment failed. Please try again.' && errorMessage.trim() !== '') {
          console.log('Showing specific error message:', errorMessage);
          this.toast.showDanger(errorMessage, true); // Don't add "Please contact support"
        } else {
          console.log('Showing generic error message');
          this.toast.showDanger('Payment failed. Please try again.');
        }
        this.modal.dismiss('payment-failed');
      } else if (error.message) {
        this.toast.showDanger(error.message);
        this.modal.dismiss('payment-failed');
      } else {
        this.toast.showDanger('Payment failed. Please try again.');
        this.modal.dismiss('payment-failed');
      }

      console.error('Payment error:', error);
    }
  }

  getStepTitle(): string {
    switch (this.step) {
      case 1: return 'Payment Amount';
      case 2: return 'Card Information';
      case 3: return 'Billing Information';
      default: return 'Payment';
    }
  }
}